import {
  AppBskyFeedLike,
  AppBskyFeedPost,
  AppBskyFeedRepost,
  AppBskyGraphFollow,
  AtUri,
} from '@atproto/api'
import { db } from '../cache/db'
import type { AiProvider } from './aiNames'

export type HandleFeatures = {
  handle: string
  totals: {
    posts: number
    replies: number
    likes: number
    reposts: number
    follows: number
  }
  /** Hour-of-day histogram (UTC), length 24 */
  hourBuckets: number[]
  /** Inter-action gap stats (ms) */
  medianGapMs: number
  meanGapMs: number
  subSecondBursts: number
  zeroMsTimestamps: number
  /** Sample of recent post text (truncated, max 50 lines) */
  samplePosts: string[]
  /** Top 15 hashtags by count */
  topHashtags: { tag: string; count: number }[]
  /** Top 10 DIDs they reply to most */
  topReplyTargets: { did: string; count: number }[]
  /** First / last action ISO timestamps */
  earliest: string | null
  latest: string | null
  /** Number of distinct subject URIs this handle liked (post they ❤️'d). */
  likedSubjectCount: number
  /** Number of distinct reply-parent URIs they replied to. */
  replyParentCount: number
  /**
   * Histogram of moderation/self labels across the things THEY LIKED. Built
   * from cached media_items for the handle (so it covers visual content —
   * porn, sexual, nudity, graphic-media, etc — plus any record self-labels
   * we've already hydrated).
   */
  likedLabelCounts: Record<string, number>
  /** Liked image vs video counts (also from media_items). */
  likedImageCount: number
  likedVideoCount: number
  /**
   * Top hashtags pulled from liked-post text we've already cached locally
   * (db.posts). Sparse if hydration hasn't run for that handle. Useful for
   * surfacing things like "anime" / "art" affinity even when there are no
   * formal labels.
   */
  likedHashtags: { tag: string; count: number }[]
  /** Whether the cached label/hashtag profile is meaningful. */
  contentProfileSamples: number
  /** The single most-frequent moderation label across the things they
   *  liked (e.g. "porn", "sexual", "graphic-media"). null when there is no
   *  labelled content cached yet. */
  dominantLabel: string | null
  /** Share (0..1) of liked-content labels that are the dominant one. A
   *  high value (≥0.25) means the account has a clear "purpose" / theme. */
  dominantLabelShare: number
  /** Best-guess purpose string built from dominantLabel + media counts +
   *  top liked hashtags, e.g. "NSFW art (porn 78%) · #anime #yuri". */
  themePurpose: string
  /** Sum of adult / sexual / nudity / graphic-media label hits — the
   *  primary content indicator for same-operator clusters. */
  adultLabelHits: number
  /** Share of total label hits that are adult/sexual/nudity (0..1). */
  adultShare: number
}

export type PairOverlap = {
  i: number
  j: number
  /** Distinct seconds at which both handles had ≥1 action */
  secondOverlap: number
  /** Distinct ms */
  msOverlap: number
  /** Distinct subject URIs both handles liked. */
  likeOverlapCount: number
  /** Union of liked subject URIs across the pair. */
  likeUnionCount: number
  /** Jaccard similarity of liked subjects (overlap / union). 0..1 */
  likeJaccard: number
  /** Distinct reply-parent URIs both handles replied to. */
  replyOverlapCount: number
  replyUnionCount: number
  replyJaccard: number
  /**
   * Cosine similarity of the two handles' liked-content label histograms.
   * 1 == identical taste profile, 0 == disjoint. Sparse data → 0.
   */
  labelCosine: number
  /** Cosine over the merged hashtag vectors (own #tags + liked #tags). */
  tagCosine: number
  /** Cosine over the 24-bucket UTC hour-of-day activity vectors. High
   *  (≥0.7) means both accounts are active in the same hours — a strong
   *  same-operator hint when each account has its own content purpose. */
  timingCosine: number
  /** Up to 100 actually-overlapping liked subject URIs (UI evidence — not
   *  forwarded to the AI). */
  sharedLikedSubjectSample: string[]
  /** Up to 100 actually-overlapping reply-parent URIs. */
  sharedReplyParentSample: string[]
}

/** A consecutive run of activity by one handle inside the merged cluster
 *  timeline. Used to render the hop strip + hop sequence. */
export type HopSession = {
  handle: string
  startTs: string
  endTs: string
  /** Number of cached actions inside this session (likes/posts/etc combined). */
  count: number
}

export type ClusterMember = {
  handle: string
  /** 0..1 — average pairwise same-operator score against the rest of the
   *  cluster (timing match + adult-share alignment). Higher = more central
   *  / more likely to be in the operator's "core" group. */
  centrality: number
  /** True when centrality crosses the core threshold (default 0.65). */
  isCore: boolean
  themePurpose: string
}

export type HopAnalysis = {
  /** Handles that the system groups together as a candidate same-operator
   *  cluster: each has substantial adult-labelled content, timing patterns
   *  align, and there are ≥2 such handles. */
  cluster: string[]
  /** Cluster members sorted by likelihood of being the same operator
   *  (descending centrality). Use this for the "WHICH accounts are the
   *  same person" ranking — the top of the list is the operator's core
   *  group. */
  ranking: ClusterMember[]
  /** Chronological dwell sessions across the cluster. */
  sessions: HopSession[]
  /** Total handle-switch events across the merged timeline. */
  switches: number
  /** Average gap (ms) between successive switches. */
  avgGapBetweenSwitchesMs: number
  /** Median session length (count of actions). */
  medianSessionActions: number
  /** Compact ordered text sequence, e.g. "alpha → beta → alpha → gamma". */
  hopSequence: string
}

export type CorrelationInput = {
  handles: HandleFeatures[]
  pairwise: PairOverlap[]
  hop: HopAnalysis | null
}

const HASHTAG_RE = /(?:^|\s)#([\p{L}\p{N}_]{2,40})/gu

function bucketTimestamps(ts: number[]) {
  const ms = new Set<number>()
  const sec = new Set<number>()
  for (const t of ts) {
    ms.add(t)
    sec.add(Math.floor(t / 1000))
  }
  return { ms, sec }
}

function intersect<T>(a: Set<T>, b: Set<T>): number {
  let n = 0
  const small = a.size < b.size ? a : b
  const big = a.size < b.size ? b : a
  for (const v of small) if (big.has(v)) n++
  return n
}

export type HandleSets = {
  likedSubjects: Set<string>
  replyParents: Set<string>
  labelVector: Map<string, number>
}

export async function gatherFeatures(
  handle: string,
): Promise<{ features: HandleFeatures; sets: HandleSets }> {
  const h = handle.toLowerCase()
  const [posts, likes, reposts, follows, likedMedia] = await Promise.all([
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.post']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.like']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.repost']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.graph.follow']).toArray(),
    db.media_items.where('[ownerHandle+verb]').equals([h, 'liked']).toArray(),
  ])

  const ts: number[] = []
  let postCount = 0
  let replyCount = 0
  const hashtagCount = new Map<string, number>()
  const replyTargets = new Map<string, number>()
  const samples: string[] = []
  const replyParents = new Set<string>()

  for (const p of posts) {
    if (!AppBskyFeedPost.isRecord(p.value)) continue
    const t = new Date(p.value.createdAt).getTime()
    if (Number.isNaN(t)) continue
    ts.push(t)
    if (p.value.reply?.parent?.uri) {
      replyCount++
      replyParents.add(p.value.reply.parent.uri)
      try {
        const at = new AtUri(p.value.reply.parent.uri)
        replyTargets.set(at.hostname, (replyTargets.get(at.hostname) ?? 0) + 1)
      } catch {
        /* ignore */
      }
    } else {
      postCount++
    }
    if (typeof p.value.text === 'string' && p.value.text.length > 0 && samples.length < 50) {
      samples.push(p.value.text.slice(0, 280))
      let m: RegExpExecArray | null
      while ((m = HASHTAG_RE.exec(p.value.text)) !== null) {
        const tag = m[1].toLowerCase()
        hashtagCount.set(tag, (hashtagCount.get(tag) ?? 0) + 1)
      }
    }
  }

  const likedSubjects = new Set<string>()
  for (const l of likes) {
    if (!AppBskyFeedLike.isRecord(l.value)) continue
    const t = new Date(l.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
    if (typeof l.value.subject?.uri === 'string') likedSubjects.add(l.value.subject.uri)
  }
  for (const r of reposts) {
    if (!AppBskyFeedRepost.isRecord(r.value)) continue
    const t = new Date(r.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }
  for (const f of follows) {
    if (!AppBskyGraphFollow.isRecord(f.value)) continue
    const t = new Date(f.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }

  // Content-taste profile from liked media (porn, sexual, nudity,
  // graphic-media etc — plus image/video bucket).
  const labelVector = new Map<string, number>()
  let likedImageCount = 0
  let likedVideoCount = 0
  const seenMediaSubjects = new Set<string>()
  for (const m of likedMedia) {
    const subjectKey = `${m.did}|${m.rkey}`
    const subjectFirstHit = !seenMediaSubjects.has(subjectKey)
    seenMediaSubjects.add(subjectKey)
    if (subjectFirstHit) {
      if (m.isVideo) likedVideoCount += 1
      else likedImageCount += 1
    }
    for (const lab of m.labels ?? []) {
      labelVector.set(lab, (labelVector.get(lab) ?? 0) + 1)
    }
  }

  // Opportunistic hashtag profile from cached liked posts (sparse — we only
  // have what db.posts has hydrated so far, but it grows naturally with use).
  const likedHashtagCount = new Map<string, number>()
  let contentProfileSamples = labelVector.size > 0 ? seenMediaSubjects.size : 0
  if (likedSubjects.size > 0) {
    const sample = Array.from(likedSubjects).slice(0, 500)
    const cached = await Promise.all(sample.map((u) => db.posts.get(u).catch(() => undefined)))
    for (const row of cached) {
      if (!row) continue
      const v = row.value as { text?: string; embed?: { $type?: string } }
      if (typeof v?.text === 'string') {
        let m: RegExpExecArray | null
        while ((m = HASHTAG_RE.exec(v.text)) !== null) {
          const tag = m[1].toLowerCase()
          likedHashtagCount.set(tag, (likedHashtagCount.get(tag) ?? 0) + 1)
        }
      }
      contentProfileSamples += 1
    }
  }

  ts.sort((a, b) => a - b)
  const hourBuckets = new Array(24).fill(0) as number[]
  let zeroMs = 0
  for (const t of ts) {
    hourBuckets[new Date(t).getUTCHours()]++
    if (t % 1000 === 0) zeroMs++
  }
  const gaps: number[] = []
  for (let i = 1; i < ts.length; i++) gaps.push(ts[i] - ts[i - 1])
  gaps.sort((a, b) => a - b)
  const median = gaps.length === 0 ? 0 : gaps[Math.floor(gaps.length / 2)]
  const mean = gaps.length === 0 ? 0 : gaps.reduce((s, g) => s + g, 0) / gaps.length
  const subSecond = gaps.filter((g) => g < 1000).length

  const topHashtags = Array.from(hashtagCount.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15)
  const topReplyTargets = Array.from(replyTargets.entries())
    .map(([did, count]) => ({ did, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
  const likedHashtags = Array.from(likedHashtagCount.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15)

  const likedLabelCounts: Record<string, number> = {}
  for (const [k, v] of labelVector) likedLabelCounts[k] = v

  // Derive each handle's "purpose": the dominant moderation label across the
  // things they liked, plus its share of the total. Operators who run multiple
  // themed accounts give each account a focused purpose (one for NSFW, one for
  // anime, one for wholesome, etc), so a high dominantLabelShare is what makes
  // the account a member of a candidate same-operator cluster.
  let dominantLabel: string | null = null
  let dominantCount = 0
  let totalLabelHits = 0
  for (const [k, v] of labelVector) {
    totalLabelHits += v
    if (v > dominantCount) {
      dominantCount = v
      dominantLabel = k
    }
  }
  const dominantLabelShare = totalLabelHits === 0 ? 0 : dominantCount / totalLabelHits

  const adultLabels = new Set([
    'porn',
    'sexual',
    'nudity',
    'sexual-figurative',
    'graphic-media',
    'adult',
    'nsfw',
  ])
  let adultLabelHits = 0
  for (const [k, v] of labelVector) {
    if (adultLabels.has(k)) adultLabelHits += v
  }
  const adultShare = totalLabelHits === 0 ? 0 : adultLabelHits / totalLabelHits

  const purposeBits: string[] = []
  if (dominantLabel) {
    purposeBits.push(`${dominantLabel} ${(dominantLabelShare * 100).toFixed(0)}%`)
  } else if (likedImageCount + likedVideoCount > 0) {
    purposeBits.push('media (no labels)')
  }
  const topLikedTags = likedHashtags.slice(0, 3).map((t) => `#${t.tag}`)
  if (topLikedTags.length > 0) purposeBits.push(topLikedTags.join(' '))
  const themePurpose = purposeBits.join(' · ') || 'no theme detected'

  const features: HandleFeatures = {
    handle: h,
    totals: {
      posts: postCount,
      replies: replyCount,
      likes: likes.length,
      reposts: reposts.length,
      follows: follows.length,
    },
    hourBuckets,
    medianGapMs: Math.round(median),
    meanGapMs: Math.round(mean),
    subSecondBursts: subSecond,
    zeroMsTimestamps: zeroMs,
    samplePosts: samples,
    topHashtags,
    topReplyTargets,
    earliest: ts.length > 0 ? new Date(ts[0]).toISOString() : null,
    latest: ts.length > 0 ? new Date(ts[ts.length - 1]).toISOString() : null,
    likedSubjectCount: likedSubjects.size,
    replyParentCount: replyParents.size,
    likedLabelCounts,
    likedImageCount,
    likedVideoCount,
    likedHashtags,
    contentProfileSamples,
    dominantLabel,
    dominantLabelShare,
    themePurpose,
    adultLabelHits,
    adultShare,
  }
  return { features, sets: { likedSubjects, replyParents, labelVector } }
}

async function gatherTimestampsOnly(handle: string): Promise<number[]> {
  const h = handle.toLowerCase()
  const all: number[] = []
  await db.records
    .where('handle')
    .equals(h)
    .each((r) => {
      const v = r.value as { createdAt?: string }
      const c = v?.createdAt
      if (typeof c === 'string') {
        const t = new Date(c).getTime()
        if (!Number.isNaN(t)) all.push(t)
      }
    })
  return all
}

export const MAX_CORRELATION_HANDLES = 10

function intersectSize<T>(a: Set<T>, b: Set<T>): number {
  let n = 0
  const small = a.size < b.size ? a : b
  const big = a.size < b.size ? b : a
  for (const v of small) if (big.has(v)) n++
  return n
}

function jaccard(overlap: number, sizeA: number, sizeB: number): number {
  const union = sizeA + sizeB - overlap
  return union === 0 ? 0 : overlap / union
}

function cosineFromMaps(a: Map<string, number>, b: Map<string, number>): number {
  if (a.size === 0 || b.size === 0) return 0
  let dot = 0
  let magA = 0
  let magB = 0
  for (const v of a.values()) magA += v * v
  for (const v of b.values()) magB += v * v
  const small = a.size < b.size ? a : b
  const big = a.size < b.size ? b : a
  for (const [k, v] of small) {
    const o = big.get(k)
    if (o) dot += v * o
  }
  const denom = Math.sqrt(magA) * Math.sqrt(magB)
  return denom === 0 ? 0 : dot / denom
}

const SHARED_SAMPLE_CAP = 100

function sampleIntersection(a: Set<string>, b: Set<string>, cap: number): string[] {
  const small = a.size < b.size ? a : b
  const big = a.size < b.size ? b : a
  const out: string[] = []
  for (const v of small) {
    if (big.has(v)) {
      out.push(v)
      if (out.length >= cap) break
    }
  }
  return out
}

/** Score 0..1 for whether a pair looks like the same operator on the
 *  primary signals: substantial adult-content presence on each side and a
 *  matching hour-of-day timing fingerprint. Used both for the cluster gate
 *  and for ranking members within the cluster. */
function pairOperatorScore(
  a: HandleFeatures,
  b: HandleFeatures,
  p: PairOverlap,
): number {
  const minLikes = Math.min(a.likedSubjectCount, b.likedSubjectCount)
  if (minLikes < 50) return 0
  const adultPresence =
    Math.min(a.adultShare, b.adultShare) >= 0.05 ||
    Math.min(a.adultLabelHits, b.adultLabelHits) >= 25
  if (!adultPresence) return 0
  // Bring timingCosine forward but compress the lower tail: the goal is to
  // reward strong timing matches and avoid rewarding random alignment.
  const timing = Math.max(0, (p.timingCosine - 0.5) / 0.5)
  // Adult-share alignment — both sides being ~adult-heavy in similar
  // proportion is the indicator the user called out.
  const adultAlign = 1 - Math.min(1, Math.abs(a.adultShare - b.adultShare) / 0.5)
  return Math.max(0, Math.min(1, 0.65 * timing + 0.35 * adultAlign))
}

function buildHopAnalysis(
  handles: string[],
  features: HandleFeatures[],
  pairwise: PairOverlap[],
  tsLists: number[][],
): HopAnalysis | null {
  // Cluster gate: each side has meaningful adult-labelled content, timing
  // patterns line up, and there is enough volume to judge. We deliberately
  // do NOT require similar tags or low actual-post overlap — operators
  // with multi-themed accounts will look DIFFERENT on those axes.
  const parent = handles.map((_, i) => i)
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]]
      x = parent[x]
    }
    return x
  }
  const union = (a: number, b: number): void => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent[ra] = rb
  }

  const pairScore = new Map<string, number>()
  for (const p of pairwise) {
    const a = features[p.i]
    const b = features[p.j]
    const score = pairOperatorScore(a, b, p)
    pairScore.set(`${p.i}-${p.j}`, score)
    pairScore.set(`${p.j}-${p.i}`, score)
    if (score >= 0.6) union(p.i, p.j)
  }

  const groups = new Map<number, number[]>()
  for (let i = 0; i < handles.length; i++) {
    const r = find(i)
    if (!groups.has(r)) groups.set(r, [])
    groups.get(r)!.push(i)
  }
  let bestGroup: number[] = []
  for (const g of groups.values()) {
    if (g.length >= 2 && g.length > bestGroup.length) bestGroup = g
  }
  if (bestGroup.length < 2) return null

  // Tag every cached timestamp with its handle and merge-sort across the
  // cluster. Then compress consecutive same-handle runs into sessions.
  type Stamp = { ts: number; handle: string }
  const merged: Stamp[] = []
  for (const idx of bestGroup) {
    const handle = handles[idx]
    for (const t of tsLists[idx]) merged.push({ ts: t, handle })
  }
  if (merged.length < 2) return null
  merged.sort((a, b) => a.ts - b.ts)

  const sessions: HopSession[] = []
  const switchGaps: number[] = []
  let curHandle = merged[0].handle
  let curStart = merged[0].ts
  let curEnd = merged[0].ts
  let curCount = 1
  let lastSwitchTs = 0
  for (let i = 1; i < merged.length; i++) {
    const m = merged[i]
    if (m.handle === curHandle) {
      curCount += 1
      curEnd = m.ts
      continue
    }
    sessions.push({
      handle: curHandle,
      startTs: new Date(curStart).toISOString(),
      endTs: new Date(curEnd).toISOString(),
      count: curCount,
    })
    if (lastSwitchTs > 0) switchGaps.push(m.ts - lastSwitchTs)
    lastSwitchTs = m.ts
    curHandle = m.handle
    curStart = m.ts
    curEnd = m.ts
    curCount = 1
  }
  sessions.push({
    handle: curHandle,
    startTs: new Date(curStart).toISOString(),
    endTs: new Date(curEnd).toISOString(),
    count: curCount,
  })

  const switches = sessions.length - 1
  const avgGap =
    switchGaps.length === 0
      ? 0
      : Math.round(switchGaps.reduce((s, g) => s + g, 0) / switchGaps.length)
  const sessionActions = sessions.map((s) => s.count).sort((a, b) => a - b)
  const median = sessionActions[Math.floor(sessionActions.length / 2)] ?? 0

  // Build a compact human sequence — collapse 4+ identical consecutive
  // tokens to "@x ×N" so it stays readable for very chatty timelines.
  const seqHandles = sessions.map((s) => s.handle)
  const sequenceTokens: string[] = []
  let i = 0
  while (i < seqHandles.length) {
    let j = i
    while (j < seqHandles.length && seqHandles[j] === seqHandles[i]) j++
    const run = j - i
    if (run >= 4) sequenceTokens.push(`@${seqHandles[i]} ×${run}`)
    else for (let k = 0; k < run; k++) sequenceTokens.push(`@${seqHandles[i]}`)
    i = j
  }

  // Centrality ranking inside the cluster: each member's average pair
  // operator-score against the other members. Higher = the member's
  // signals (adult-share + timing) align with the rest of the cluster.
  const ranking: ClusterMember[] = []
  for (const i of bestGroup) {
    let sum = 0
    let n = 0
    for (const j of bestGroup) {
      if (i === j) continue
      const s = pairScore.get(`${i}-${j}`) ?? 0
      sum += s
      n += 1
    }
    const centrality = n === 0 ? 0 : sum / n
    ranking.push({
      handle: handles[i],
      centrality: Number(centrality.toFixed(4)),
      isCore: centrality >= 0.65,
      themePurpose: features[i].themePurpose,
    })
  }
  ranking.sort((a, b) => b.centrality - a.centrality)

  return {
    cluster: bestGroup.map((i) => handles[i]),
    ranking,
    sessions,
    switches,
    avgGapBetweenSwitchesMs: avgGap,
    medianSessionActions: median,
    hopSequence: sequenceTokens.join(' → '),
  }
}

function hourBucketCosine(a: number[], b: number[]): number {
  if (a.length === 0 || b.length === 0) return 0
  let dot = 0
  let magA = 0
  let magB = 0
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i]
    magA += a[i] * a[i]
    magB += b[i] * b[i]
  }
  const denom = Math.sqrt(magA) * Math.sqrt(magB)
  return denom === 0 ? 0 : dot / denom
}

function tagVector(h: HandleFeatures): Map<string, number> {
  const v = new Map<string, number>()
  for (const t of h.topHashtags) v.set(t.tag, (v.get(t.tag) ?? 0) + t.count)
  for (const t of h.likedHashtags) v.set(t.tag, (v.get(t.tag) ?? 0) + t.count)
  return v
}

export async function buildCorrelationInput(handles: string[]): Promise<CorrelationInput> {
  const slice = handles.slice(0, MAX_CORRELATION_HANDLES)
  const gathered = await Promise.all(slice.map((h) => gatherFeatures(h)))
  const features = gathered.map((g) => g.features)
  const sets = gathered.map((g) => g.sets)
  const tsLists = await Promise.all(slice.map((h) => gatherTimestampsOnly(h)))
  const tsSets = tsLists.map(bucketTimestamps)
  const tagVectors = features.map(tagVector)
  const pairwise: PairOverlap[] = []
  for (let i = 0; i < slice.length; i++) {
    for (let j = i + 1; j < slice.length; j++) {
      const a = sets[i]
      const b = sets[j]
      const likeOverlap = intersectSize(a.likedSubjects, b.likedSubjects)
      const likeUnion = a.likedSubjects.size + b.likedSubjects.size - likeOverlap
      const replyOverlap = intersectSize(a.replyParents, b.replyParents)
      const replyUnion = a.replyParents.size + b.replyParents.size - replyOverlap
      pairwise.push({
        i,
        j,
        secondOverlap: intersect(tsSets[i].sec, tsSets[j].sec),
        msOverlap: intersect(tsSets[i].ms, tsSets[j].ms),
        likeOverlapCount: likeOverlap,
        likeUnionCount: likeUnion,
        likeJaccard: Number(
          jaccard(likeOverlap, a.likedSubjects.size, b.likedSubjects.size).toFixed(4),
        ),
        replyOverlapCount: replyOverlap,
        replyUnionCount: replyUnion,
        replyJaccard: Number(
          jaccard(replyOverlap, a.replyParents.size, b.replyParents.size).toFixed(4),
        ),
        labelCosine: Number(cosineFromMaps(a.labelVector, b.labelVector).toFixed(4)),
        tagCosine: Number(cosineFromMaps(tagVectors[i], tagVectors[j]).toFixed(4)),
        timingCosine: Number(
          hourBucketCosine(features[i].hourBuckets, features[j].hourBuckets).toFixed(4),
        ),
        sharedLikedSubjectSample: sampleIntersection(
          a.likedSubjects,
          b.likedSubjects,
          SHARED_SAMPLE_CAP,
        ),
        sharedReplyParentSample: sampleIntersection(
          a.replyParents,
          b.replyParents,
          SHARED_SAMPLE_CAP,
        ),
      })
    }
  }
  const hop = buildHopAnalysis(slice, features, pairwise, tsLists)
  return { handles: features, pairwise, hop }
}

/* ───────── AI prompt + provider call ───────── */

export type CorrelationVerdict = {
  pairs: Array<{
    a: string
    b: string
    /** 0..1 */
    likelihoodSamePerson: number
    /** "low" / "medium" / "high" / "very high" */
    confidence: string
    /** Brief one-paragraph reasoning */
    reasoning: string
    /** Concrete signals the model leaned on */
    signals: string[]
  }>
  /** Overall verdict for the whole set (when handles.length >= 3) */
  groupVerdict: {
    likelihoodAllSame: number
    reasoning: string
  }
  /** AI's narration of the operator hop pattern, when a candidate cluster
   *  was passed in. Null/absent if no cluster was supplied. */
  hopOrder?: {
    cluster: string[]
    /** Free-form ordered hop description, e.g.
     *  "operator appears to alternate alpha → beta → alpha, with bursts on
     *   beta during US morning". */
    narrative: string
    /** 0..1 confidence that the cluster is one operator. */
    confidence: number
  } | null
  /** Holistic group-level ranking the AI provides when given a candidate
   *  cluster: which accounts in the cluster are most likely the same
   *  person, with a per-account score. Null when there's no cluster. */
  groupRanking?: Array<{
    handle: string
    /** 0..1 — likelihood this account is part of the operator's core
     *  same-person group, holistically (not just vs one other). */
    score: number
    /** "core" | "peripheral" | "unrelated" */
    bucket: string
    /** One-line reason citing concrete numbers (timing, adultShare,
     *  themePurpose, switches involving this account). */
    reason: string
  }> | null
}

const SYSTEM_PROMPT = `You are an analyst inspecting Bluesky account telemetry for same-person / shared-operator
accounts. The user's mental model: an operator who runs multiple accounts assigns each
account its own PURPOSE (one for NSFW art, another for anime, another for wholesome). So
the SAME-PERSON signal is NOT "their interests overlap" — each account looks different on
the surface. The signal is:

1. ADULT-CONTENT PRESENCE — porn / sexual / nudity / sexual-figurative / graphic-media /
   adult / nsfw labels appear with substantial frequency in the LIKES of multiple accounts
   in the candidate cluster. adultShare and adultLabelHits are precomputed per handle.
2. TIMING FINGERPRINT — timingCosine over 24-bucket hour-of-day vectors, plus subSecondBursts
   / msOverlap / secondOverlap from pair stats. Same operator = same active hours.
3. EACH ACCOUNT'S OWN PURPOSE — themePurpose / dominantLabel / dominantLabelShare per handle.
   Different dominant labels across cluster members is EXPECTED, not disqualifying. Tag and
   content overlap can be low — that's fine; that's the operator splitting purposes.

Actual-post overlap (likeJaccard, replyJaccard) and tag/label cosine are SECONDARY. They
are useful as confirmation (very low likeJaccard + matching adultShare + matching timing
strengthens the case) but not required, and high overlap on any of them is more often a
shared-community signal than a same-person signal.

Return STRICT JSON, no prose, no markdown. Schema:
{
  "pairs": [
    {
      "a": "<handle>",
      "b": "<handle>",
      "likelihoodSamePerson": <number 0..1>,
      "confidence": "low" | "medium" | "high" | "very high",
      "reasoning": "<one short paragraph that CITES specific numbers from the input — adultShare, timingCosine, msOverlap, themePurpose, etc>",
      "signals": [ "<short concrete bullet, must cite a number or label>", ... up to 6 ]
    }
  ],
  "groupVerdict": {
    "likelihoodAllSame": <number 0..1>,
    "reasoning": "<one short paragraph>"
  },
  "hopOrder": null | {
    "cluster": [ "<handle>", ... ],
    "narrative": "<one short paragraph describing the order the operator seems to hop between accounts in the cluster, citing session counts / dwell length / time-of-day from the hop summary>",
    "confidence": <number 0..1>
  },
  "groupRanking": null | [
    {
      "handle": "<handle from the candidate cluster>",
      "score": <number 0..1>,
      "bucket": "core" | "peripheral" | "unrelated",
      "reason": "<one short sentence citing this handle's adultShare, themePurpose and timingCosine vs others in the cluster>"
    }, ...
  ]
}

CRITICAL — when the input includes a non-null "hop" object, you MUST output a non-null
"groupRanking" sorted DESCENDING by score. The ranking is the user's most important
output: it answers "which accounts in this cluster are most likely the same person". Use
the precomputed hop.ranking centrality scores as your starting point, but you may
re-weight based on adultShare and timingCosine. Mark accounts that look unconnected to the
operator as bucket="unrelated" with a low score.

Be specific. Every "reasoning" / "signal" / "reason" string MUST reference at least one
concrete number or label from the input — don't say "similar timing", say "timingCosine
0.91 between alpha and beta, both peak hourBucket 23 UTC". Don't say "shared NSFW interest",
say "adultShare alpha=0.78 beta=0.71 — porn 412 / sexual 188 vs porn 367 / sexual 142".

Be skeptical of small samples. If either handle has <50 likes, say so and keep confidence
low. Voice/style similarity from sample posts is weak evidence on its own.`

function compactInput(input: CorrelationInput): string {
  const compactPair = (p: PairOverlap) => ({
    i: p.i,
    j: p.j,
    secondOverlap: p.secondOverlap,
    msOverlap: p.msOverlap,
    likeOverlapCount: p.likeOverlapCount,
    likeUnionCount: p.likeUnionCount,
    likeJaccard: p.likeJaccard,
    replyOverlapCount: p.replyOverlapCount,
    replyUnionCount: p.replyUnionCount,
    replyJaccard: p.replyJaccard,
    labelCosine: p.labelCosine,
    tagCosine: p.tagCosine,
    timingCosine: p.timingCosine,
  })
  const compactHop = input.hop
    ? {
        cluster: input.hop.cluster,
        ranking: input.hop.ranking,
        switches: input.hop.switches,
        avgGapBetweenSwitchesMs: input.hop.avgGapBetweenSwitchesMs,
        medianSessionActions: input.hop.medianSessionActions,
        // Only the first 8 + last 8 sessions — enough to establish the
        // hop pattern without flooding the prompt for 50k-action timelines.
        firstSessions: input.hop.sessions.slice(0, 8),
        lastSessions: input.hop.sessions.slice(-8),
        totalSessions: input.hop.sessions.length,
        hopSequencePreview: input.hop.hopSequence.slice(0, 600),
      }
    : null
  const compact = {
    handles: input.handles.map((h) => ({
      handle: h.handle,
      totals: h.totals,
      hourBuckets: h.hourBuckets,
      medianGapMs: h.medianGapMs,
      meanGapMs: h.meanGapMs,
      subSecondBursts: h.subSecondBursts,
      zeroMsTimestamps: h.zeroMsTimestamps,
      samplePosts: h.samplePosts.slice(0, 10),
      topHashtags: h.topHashtags,
      topReplyTargets: h.topReplyTargets,
      earliest: h.earliest,
      latest: h.latest,
      likedSubjectCount: h.likedSubjectCount,
      replyParentCount: h.replyParentCount,
      likedLabelCounts: h.likedLabelCounts,
      likedImageCount: h.likedImageCount,
      likedVideoCount: h.likedVideoCount,
      likedHashtags: h.likedHashtags,
      contentProfileSamples: h.contentProfileSamples,
      dominantLabel: h.dominantLabel,
      dominantLabelShare: h.dominantLabelShare,
      themePurpose: h.themePurpose,
      adultLabelHits: h.adultLabelHits,
      adultShare: h.adultShare,
    })),
    pairwise: input.pairwise.map(compactPair),
    hop: compactHop,
  }
  return JSON.stringify(compact)
}

const DEFAULT_MODEL: Record<AiProvider, string> = {
  openai: 'gpt-4o-mini',
  anthropic: 'claude-haiku-4-5-20251001',
  gemini: 'gemini-1.5-flash',
  xai: 'grok-2-latest',
}

async function callOpenAi(payload: string, apiKey: string, baseUrl: string, model: string): Promise<string> {
  const r = await fetch(`${baseUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: payload },
      ],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { choices?: Array<{ message?: { content?: string } }> }
  return data.choices?.[0]?.message?.content ?? ''
}

async function callAnthropic(payload: string, apiKey: string, model: string): Promise<string> {
  const r = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 4096,
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: payload }],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { content?: Array<{ type?: string; text?: string }> }
  const block = (data.content ?? []).find((b) => b.type === 'text')
  return block?.text ?? ''
}

async function callGemini(payload: string, apiKey: string, model: string): Promise<string> {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
      contents: [{ role: 'user', parts: [{ text: payload }] }],
      generationConfig: { responseMimeType: 'application/json' },
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>
  }
  return data.candidates?.[0]?.content?.parts?.[0]?.text ?? ''
}

function tryParseJson<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T
  } catch {
    /* fall through */
  }
  const m = s.match(/\{[\s\S]*\}/)
  if (!m) return null
  try {
    return JSON.parse(m[0]) as T
  } catch {
    return null
  }
}

export async function runCorrelation(opts: {
  handles: string[]
  provider: AiProvider
  apiKey: string
  model?: string
}): Promise<{ input: CorrelationInput; verdict: CorrelationVerdict }> {
  if (opts.handles.length < 2) throw new Error('need at least two handles')
  if (!opts.apiKey) throw new Error('AI api key required')
  const input = await buildCorrelationInput(opts.handles)
  const payload = compactInput(input)
  const model = opts.model || DEFAULT_MODEL[opts.provider]
  let raw = ''
  switch (opts.provider) {
    case 'openai':
      raw = await callOpenAi(payload, opts.apiKey, 'https://api.openai.com', model)
      break
    case 'xai':
      raw = await callOpenAi(payload, opts.apiKey, 'https://api.x.ai', model)
      break
    case 'anthropic':
      raw = await callAnthropic(payload, opts.apiKey, model)
      break
    case 'gemini':
      raw = await callGemini(payload, opts.apiKey, model)
      break
  }
  const verdict = tryParseJson<CorrelationVerdict>(raw)
  if (!verdict) throw new Error('AI returned an unparseable response')
  return { input, verdict }
}
